// src/components/financials/GeneralLedgerView.js
// Updated: 01/27/2026 - Enforced "Locked" UI for Reconciled Transactions

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, TextField, Table, TableBody, TableCell, 
  TableHead, TableRow, Chip, Tooltip, CircularProgress, MenuItem, IconButton 
} from '@mui/material';
import { VerifiedUser, Receipt, Payment, SwapHoriz, CheckCircle, FilterAlt, Lock } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const GeneralLedgerView = () => {
  const { userProfile, currentUser } = useAuth();
  const [ledgerItems, setLedgerItems] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [filterType, setFilterType] = useState('all'); 
  const [selectedFund, setSelectedFund] = useState('all'); 
  
  const [userMap, setUserMap] = useState({});
  const [fundsList, setFundsList] = useState([]); 
  const [fundMap, setFundMap] = useState({});     

  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`);

  const churchId = userProfile?.churchId;
  const isSuperUser = ['Super Administrator', 'Church Administrator'].includes(userProfile?.role);
  const isFinanceUser = userProfile?.financeRole === 'admin' || userProfile?.role === 'Finance Admin';

  const canApproveRow = (row) => {
      // Once reconciled, NO ONE can revert/approve again. It is locked.
      if (row.reconciled) return false;

      if (isSuperUser) return true;
      if (isFinanceUser) {
          if (row.createdBy === currentUser.uid) return false;
          return true;
      }
      return false;
  };

  useEffect(() => {
    const fetchMetadata = async () => {
        if (!churchId) return;
        try {
            // Users
            const usersRef = collection(db, 'users');
            const userQ = query(usersRef, where('churchId', '==', churchId));
            const userSnap = await getDocs(userQ);
            const uMap = {};
            userSnap.forEach(doc => {
                const d = doc.data();
                uMap[doc.id] = d.displayName || `${d.firstName || ''} ${d.lastName || ''}`.trim() || 'Unknown User';
            });
            setUserMap(uMap);

            // Funds
            const fundsRef = collection(db, 'churches', churchId, 'financials', 'data', 'funds');
            const fundSnap = await getDocs(fundsRef);
            const fMap = {};
            const fList = [];
            
            fundSnap.forEach(doc => {
                const d = doc.data();
                const safeName = d.name || "Unnamed Fund"; 
                fMap[doc.id] = safeName;
                fList.push({ id: doc.id, name: safeName });
            });
            
            setFundMap(fMap);
            setFundsList(fList.sort((a, b) => (a.name || "").localeCompare(b.name || "")));

        } catch (e) { console.error("Metadata Error:", e); }
    };
    fetchMetadata();
  }, [churchId]); 

  const fetchLedger = async () => {
    if (!churchId) return;
    setLoading(true);
    try {
      // 1. Fetch Income
      const txRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');
      const txQ = query(
        txRef,
        where('status', '==', 'posted'),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc'),
        limit(300)
      );
      const txSnap = await getDocs(txQ);
      const incomeData = txSnap.docs.map(d => {
          const data = d.data();
          return { id: d.id, type: data.type || 'income', ...data };
      });

      // 2. Fetch Expenses
      const expRef = collection(db, 'churches', churchId, 'financials', 'data', 'expenses');
      const expQ = query(
        expRef,
        where('status', '==', 'approved'),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc'),
        limit(300)
      );
      const expSnap = await getDocs(expQ);
      const expenseData = expSnap.docs.map(d => ({ id: d.id, type: 'expense', ...d.data() }));

      let combined = [...incomeData, ...expenseData];
      
      // 3. Filter by Fund
      if (selectedFund !== 'all') {
          combined = combined.filter(item => {
              if (item.fundId === selectedFund) return true;
              if (item.splits && item.splits.some(s => s.fundId === selectedFund)) return true;
              return false;
          });
      }

      // 4. Filter by Status
      if (filterType === 'reconciled') combined = combined.filter(t => t.reconciled === true);
      else if (filterType === 'pending') combined = combined.filter(t => !t.reconciled);

      // 5. Sort
      combined.sort((a, b) => new Date(b.date) - new Date(a.date));

      setLedgerItems(combined);
    } catch (e) {
      alert("Error loading ledger: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLedger();
  }, [churchId, startDate, endDate, filterType, selectedFund]); 

  const handleVerify = async (item) => {
      // Logic Check: Cannot re-verify
      if (item.reconciled) return;

      if (!window.confirm("Verify and Lock this transaction? This cannot be undone.")) return;
      
      try {
          const collectionName = item.type === 'expense' ? 'expenses' : 'transactions';
          const ref = doc(db, 'churches', churchId, 'financials', 'data', collectionName, item.id);
          
          await updateDoc(ref, {
              reconciled: true,
              reconciledAt: serverTimestamp(),
              reconciledBy: currentUser.uid
          });

          setLedgerItems(prev => prev.map(i => {
              if (i.id === item.id) {
                  return { 
                      ...i, 
                      reconciled: true, 
                      reconciledAt: { seconds: Date.now() / 1000 }, 
                      reconciledBy: currentUser.uid 
                  };
              }
              return i;
          }));

      } catch (e) {
          alert("Verification failed: " + e.message);
      }
  };

  const formatTime = (ts) => {
      if (!ts) return "";
      return new Date(ts.seconds * 1000).toLocaleString();
  };

  const getUserName = (uid) => {
      if (!uid) return "System";
      return userMap[uid] || `User: ${uid.substring(0, 6)}...`; 
  };

  const getFundName = (id) => fundMap[id] || id || "General";

  const getRowVisuals = (row) => {
      const amt = parseFloat(row.amount) || 0;
      if (row.type === 'transfer' || row.type === 'adjustment') {
          return {
              icon: <SwapHoriz color="info" fontSize="small" />,
              color: amt >= 0 ? 'success.main' : 'error.main',
              sign: amt >= 0 ? '+' : '' 
          };
      }
      if (row.type === 'expense' || amt < 0) {
          return {
              icon: <Payment color="error" fontSize="small" />,
              color: 'error.main',
              sign: '' 
          };
      }
      return {
          icon: <Receipt color="success" fontSize="small" />,
          color: 'success.main',
          sign: '+'
      };
  };

  // --- REVISED TOTAL CALCULATION ---
  const totalAmount = ledgerItems.reduce((sum, item) => {
      const rawAmt = parseFloat(item.amount) || 0;
      if (item.type === 'expense' || item.type === 'bill_payment' || item.type === 'withdrawal' || item.type === 'check') {
          return sum - Math.abs(rawAmt);
      }
      return sum + rawAmt;
  }, 0);

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <VerifiedUser sx={{ mr: 1, color: 'primary.main' }} /> 
                General Ledger & Audit Trail
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                
                <TextField 
                    select size="small" label="Filter Fund" 
                    value={selectedFund} onChange={(e) => setSelectedFund(e.target.value)}
                    sx={{ width: 180 }}
                    InputProps={{ startAdornment: <FilterAlt fontSize="small" color="action" sx={{ mr: 1 }} /> }}
                >
                    <MenuItem value="all">All Funds</MenuItem>
                    {fundsList.map(f => (
                        <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                </TextField>

                <TextField 
                    select size="small" label="Status" 
                    value={filterType} onChange={(e) => setFilterType(e.target.value)}
                    sx={{ width: 150 }}
                >
                    <MenuItem value="all">Show All</MenuItem>
                    <MenuItem value="reconciled">Reconciled Only</MenuItem>
                    <MenuItem value="pending">Needs Audit</MenuItem>
                </TextField>

                <TextField type="date" size="small" label="Start" InputLabelProps={{shrink:true}} value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ width: 140 }} />
                <TextField type="date" size="small" label="End" InputLabelProps={{shrink:true}} value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ width: 140 }} />
            </Box>
        </Box>

        <Table size="small">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description / Entity</TableCell>
              <TableCell>Fund</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center" sx={{ minWidth: 140 }}>Audit Status</TableCell>
              <TableCell>Verification Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : ledgerItems.map((row) => {
              const visuals = getRowVisuals(row);
              const amountVal = parseFloat(row.amount) || 0;
              const showApproveBtn = canApproveRow(row);

              let displayAmount = amountVal;
              if (row.type === 'expense' && amountVal > 0) displayAmount = -amountVal;

              return (
              <TableRow key={row.id} hover sx={{ bgcolor: !row.reconciled ? '#fffde7' : 'inherit' }}>
                <TableCell>{row.date}</TableCell>
                <TableCell><Tooltip title={row.type}>{visuals.icon}</Tooltip></TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                        {row.type === 'expense' ? (row.payee || "Vendor") : (row.donorName || "Donor")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {row.note || row.method || row.description || row.category}
                    </Typography>
                </TableCell>
                <TableCell>
                    {row.splits && row.splits.length > 1 ? (
                        <Tooltip title={row.splits.map(s => `${getFundName(s.fundId)}: $${s.amount}`).join("\n")}>
                            <Chip label={`Split (${row.splits.length})`} size="small" color="info" variant="outlined" sx={{ cursor: 'help' }} />
                        </Tooltip>
                    ) : (
                        <Chip label={(row.splits && row.splits.length === 1) ? getFundName(row.splits[0].fundId) : getFundName(row.fundId)} size="small" variant="outlined" />
                    )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: visuals.color }}>
                    {displayAmount < 0 ? '-' : visuals.sign}${Math.abs(displayAmount).toLocaleString(undefined, {minimumFractionDigits: 2})}
                </TableCell>
                
                <TableCell align="center">
                    {row.reconciled ? (
                        <Tooltip title="Verified & Locked">
                            <Chip 
                                icon={<Lock fontSize="small" />} 
                                label="Reconciled" 
                                color="success" 
                                size="small" 
                                variant="filled" 
                            />
                        </Tooltip>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Chip label="Needs Audit" color="warning" size="small" variant="outlined" />
                            {showApproveBtn && (
                                <Tooltip title="Approve & Lock Transaction">
                                    <IconButton size="small" color="success" onClick={() => handleVerify(row)} sx={{ border: '1px solid #66bb6a' }}>
                                        <CheckCircle fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                </TableCell>

                <TableCell sx={{ maxWidth: 250 }}>
                    {row.reconciled ? (
                        <Box>
                            <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                {getUserName(row.reconciledBy)}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                                {formatTime(row.reconciledAt)}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            -- Needs Audit --
                        </Typography>
                    )}
                </TableCell>
              </TableRow>
              );
            })}

            {/* --- TOTAL ROW --- */}
            {!loading && ledgerItems.length > 0 && (
                <TableRow sx={{ bgcolor: '#eeeeee', borderTop: '2px solid #bdbdbd' }}>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        NET TOTAL (Income - Expenses):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', color: totalAmount >= 0 ? 'success.dark' : 'error.dark' }}>
                        {totalAmount >= 0 ? '+' : ''}${totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </TableCell>
                    <TableCell colSpan={2} /> 
                </TableRow>
            )}

            {!loading && ledgerItems.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No activity found for this period.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default GeneralLedgerView;